import React from 'react';
import { motion } from 'framer-motion';
import {
    Sprout,
    Wind,
    ArrowRight,
    ChevronRight,
    BookOpen,
    Activity,
    ShieldCheck,
    Leaf
} from 'lucide-react';
import trea1 from '../assets/treat1.jpeg';
import trea2 from '../assets/treat2.jpeg';

export default function Treatments() {
    const darkGreen = "#1A2E20";
    const goldAccent = "#D4AF37";
    const creamBg = "#FDFBF7";

    const bgPattern = "https://www.transparenttextures.com/patterns/natural-paper.png";
    const heroImage = "https://images.unsplash.com/photo-1617503794951-69446d3d99d1?q=80&w=2000&auto=format&fit=crop";

    const fadeIn = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-50px" },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const treatmentCategories = [
        {
            title: "Panchakarma Wisdom",
            category: "Detoxification",
            description: "A deep educational dive into the five-fold purification process intended to restore biological rhythm and cellular intelligence.",
            image: "https://images.unsplash.com/photo-1544161515-4ae6ce6db87e?q=80&w=1000",
            icon: <Activity size={24} className="text-[#D4AF37]" />
        },
        {
            title: "Hela Wedakama Herbalism",
            category: "Traditional Medicine",
            description: "Preserving the rare botanical knowledge of Sri Lankan ancestors, using indigenous oils and leaf-based therapies.",
            image: "https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?q=80&w=1000",
            icon: <Leaf size={24} className="text-[#D4AF37]" />
        },
        {
            title: "Nadi Shastra Analysis",
            category: "Diagnostic Art",
            description: "The ancient science of pulse reading to identify imbalances before they manifest as physical ailments.",
            image: "https://images.unsplash.com/photo-1600334129128-685c5582fd35?q=80&w=1000",
            icon: <ShieldCheck size={24} className="text-[#D4AF37]" />
        },
        {
            title: "Mindfulness & Sutra",
            category: "Spirituality",
            description: "Guided sessions focusing on the psychological aspects of healing through ancient chanting and meditative stillness.",
            image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1000",
            icon: <Wind size={24} className="text-[#D4AF37]" />
        }
    ];

    return (
        <div style={{ backgroundColor: creamBg, color: darkGreen }} className="min-h-screen font-sans selection:bg-[#D4AF37] selection:text-white relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-40 z-10" style={{ backgroundImage: `url(${bgPattern})` }}></div>

            {/* HERO SECTION */}
            <section className="relative h-[60vh] md:h-[65vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img src={heroImage} className="w-full h-full object-cover" alt="Ayurvedic Treatment" />
                    <div className="absolute inset-0 bg-[#1A2E20]/40"></div>
                </div>
                <div className="relative z-20 max-w-7xl mx-auto px-6 w-full pt-20">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                        <div className="flex items-center gap-2 text-white/90 text-[10px] tracking-[0.4em] uppercase mb-6 font-bold">
                            <span>Home</span>
                            <ChevronRight size={12} className="text-[#D4AF37]" />
                            <span className="text-white font-medium">Treatments & Wisdom</span>
                        </div>
                        <h1 className="font-serif text-5xl md:text-8xl text-white leading-tight">
                            Healing Through <br/>
                            <span className="italic font-light text-[#D4AF37]">Ancient Roots</span>
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* INTRO SECTION */}
            <section className="relative py-24 px-6 z-20">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div {...fadeIn}>
                        <div className="flex justify-center mb-8">
                            <Sprout color={goldAccent} size={48} />
                        </div>
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.4em] text-[11px] block mb-6">Our Educational Mission</span>
                        <h2 className="font-serif text-4xl md:text-6xl text-[#1A2E20] mb-8 leading-tight">Preserving the Priceless Science of Hela Wedakama</h2>
                        <p className="text-gray-600 text-xl font-light leading-relaxed">
                            At Boseth Wellness Foundation, we believe that healing is a form of education. Our treatments are not merely sessions, but an invitation to understand the delicate balance between your body and the natural world.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* TREATMENT GRID */}
            <section className="relative pb-32 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
                        {treatmentCategories.map((item, i) => (
                            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }} className="group">
                                <div className="relative aspect-[16/10] overflow-hidden rounded-[2rem] mb-8 shadow-2xl">
                                    <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                                    <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2">
                                        {item.icon}
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#1A2E20]">{item.category}</span>
                                    </div>
                                </div>
                                <div className="px-4">
                                    <h3 className="font-serif text-3xl md:text-4xl text-[#1A2E20] mb-4 group-hover:text-[#D4AF37] transition-colors">{item.title}</h3>
                                    <p className="text-gray-500 text-lg font-light leading-relaxed mb-6">{item.description}</p>
                                    <button className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] hover:text-[#1A2E20] transition-colors">
                                        Learn More <ArrowRight size={14} />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WISDOM HIGHLIGHT (Unified with AboutUs Editorial Style) */}
            <section className="relative py-24 bg-[#1A2E20] text-white overflow-hidden">
                <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: `url(${bgPattern})` }}></div>
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-20 items-center">
                        <motion.div {...fadeIn} className="space-y-8">
                            <h2 className="font-serif text-5xl md:text-6xl mb-8 leading-tight">
                                A Library of <br/><span className="italic font-light text-[#D4AF37]">Healing Knowledge</span>
                            </h2>
                            <p className="text-gray-300 text-xl font-light leading-relaxed">
                                Beyond physical therapy, Boseth Wellness serves as a repository for palm-leaf manuscripts and oral traditions.
                            </p>
                            <div className="flex flex-wrap gap-10">
                                <div className="flex items-center gap-4">
                                    <div className="h-12 w-12 rounded-full border border-[#D4AF37]/30 flex items-center justify-center"><BookOpen size={20} className="text-[#D4AF37]" /></div>
                                    <span className="text-xs tracking-widest uppercase font-bold">Research Papers</span>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="h-12 w-12 rounded-full border border-[#D4AF37]/30 flex items-center justify-center"><Sprout size={20} className="text-[#D4AF37]" /></div>
                                    <span className="text-xs tracking-widest uppercase font-bold">Herbal Gardens</span>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div {...fadeIn} className="relative">
                            <div className="rounded-[3rem] overflow-hidden shadow-2xl rotate-2 aspect-[4/3]">
                                <img src={trea1} className="w-full h-full object-cover" alt="Traditional Books" />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CALL TO ACTION */}
            <section className="relative py-24 px-6">
                <div className="max-w-6xl mx-auto">
                    <motion.div {...fadeIn} className="bg-[#1A2E20] rounded-[3rem] p-10 md:p-16 flex flex-col md:flex-row justify-between items-center gap-10 shadow-2xl relative overflow-hidden">
                        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: `url(${bgPattern})` }}></div>
                        <div className="relative z-10 text-center md:text-left">
                            <h2 className="font-serif text-4xl md:text-5xl text-white mb-4">Support Our Healing Mission</h2>
                            <p className="text-white/60 text-lg font-light max-w-xl">Consult with our traditional physicians. Your engagement helps the Foundation preserve ancient Hela Wedakama.</p>
                        </div>
                        <button style={{ backgroundColor: goldAccent }} className="relative z-10 text-white px-10 py-5 rounded-full font-bold uppercase text-[12px] tracking-[0.2em] hover:bg-white hover:text-[#1A2E20] transition-all duration-500 flex items-center gap-3 shadow-xl group">
                            Request Consultation <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}